/**
 * PreProduct pre-order quantity limit
 *
 * Waits for window.preproduct, reads preproduct.max_variant_sales
 * (e.g. { "44591920742445": 50 }), applies the value as `max` on the
 * quantity input of <quantity-selector-component>, and blocks the plus
 * button / typed input from going above it.
 */
(function () {
  'use strict';

  var LIMIT_MESSAGE =
    'This pre-order has reached its limit. Please check back for additional availability or contact our warehouse at (972) 937-0337.';

  var POLL_MS = 100;
  var READY_TIMEOUT_MS = 20000;
  var alertLocked = false;
  var lastVariantId = null;

  function showLimitAlert() {
    if (alertLocked) return;
    alertLocked = true;
    window.alert(LIMIT_MESSAGE);
    setTimeout(function () {
      alertLocked = false;
    }, 400);
  }

  /* ---------- wait for PreProduct ---------- */

  function whenPreproductReady(callback) {
    var started = Date.now();

    (function check() {
      var pp = window.preproduct;
      if (pp && pp.max_variant_sales) {
        callback(pp);
        return;
      }
      if (Date.now() - started >= READY_TIMEOUT_MS) return;
      setTimeout(check, POLL_MS);
    })();
  }

  /* ---------- helpers ---------- */

  function getMaxForVariant(variantId) {
    var map = (window.preproduct && window.preproduct.max_variant_sales) || {};
    if (!Object.prototype.hasOwnProperty.call(map, String(variantId))) return null;
    var value = parseInt(map[String(variantId)], 10);
    return isNaN(value) ? null : value;
  }

  function getVariantId(scope) {
    var field = (scope || document).querySelector(
      'input[name="id"], select[name="id"]'
    );
    if (field && field.value) return String(field.value);

    var params = new URLSearchParams(window.location.search);
    return params.get('variant');
  }

  function getScope(el) {
    return (
      el.closest('form[action*="/cart/add"]') ||
      el.closest('product-info, .product, .shopify-section') ||
      document
    );
  }

  function readMax(input) {
    var max = parseInt(input.getAttribute('max'), 10);
    return isNaN(max) ? null : max;
  }

  /* ---------- guards ---------- */

  function bindGuards(component) {
    if (component.dataset.ppBound === 'true') return;
    component.dataset.ppBound = 'true';

    var input = component.querySelector('input[name="quantity"]');
    if (!input) return;

    var plus = component.querySelector('[name="plus"]');

    // Capture phase so the theme's /increaseQuantity handler never fires.
    if (plus) {
      plus.addEventListener(
        'click',
        function (event) {
          var max = readMax(input);
          if (max === null) return;
          var current = parseInt(input.value, 10) || 1;
          if (current >= max) {
            event.preventDefault();
            event.stopImmediatePropagation();
            showLimitAlert();
          }
        },
        true
      );
    }

    input.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'ArrowUp') return;
        var max = readMax(input);
        if (max === null) return;
        var current = parseInt(input.value, 10) || 1;
        if (current >= max) {
          event.preventDefault();
          showLimitAlert();
        }
      },
      true
    );

    ['input', 'change', 'blur'].forEach(function (type) {
      input.addEventListener(type, function () {
        clampInput(input);
      });
    });
  }

  function clampInput(input) {
    var max = readMax(input);
    if (max === null) return false;

    var current = parseInt(input.value, 10);
    if (isNaN(current)) return false;

    if (current > max) {
      input.value = max > 0 ? max : 1;
      showLimitAlert();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  /* ---------- apply limit ---------- */

  function applyToComponent(component) {
    var input = component.querySelector('input[name="quantity"]');
    if (!input) return;

    var variantId = getVariantId(getScope(component));
    var max = variantId ? getMaxForVariant(variantId) : null;

    bindGuards(component);

    // Not a limited pre-order variant: restore the theme's own behaviour.
    if (max === null) {
      if (input.dataset.ppApplied === 'true') {
        input.removeAttribute('max');
        delete input.dataset.ppApplied;
      }
      togglePlus(component, input, null);
      return;
    }

    input.setAttribute('max', String(max));
    input.dataset.ppApplied = 'true';

    var current = parseInt(input.value, 10) || 1;
    if (current > max) {
      input.value = max > 0 ? max : 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    togglePlus(component, input, max);
  }

  function togglePlus(component, input, max) {
    var plus = component.querySelector('[name="plus"]');
    if (!plus) return;

    if (max === null) {
      plus.removeAttribute('aria-disabled');
      plus.classList.remove('is-limit-reached');
      return;
    }

    var current = parseInt(input.value, 10) || 1;
    var atLimit = current >= max;
    plus.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
    plus.classList.toggle('is-limit-reached', atLimit);
  }

  function refresh() {
    document
      .querySelectorAll('quantity-selector-component')
      .forEach(applyToComponent);
  }

  /* ---------- observe variant + DOM changes ---------- */

  function watch() {
    var timer = null;

    var observer = new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(refresh, 120);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    ['change', 'variant:update', 'variantChange', 'popstate'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        setTimeout(refresh, 50);
      });
      window.addEventListener(evt, function () {
        setTimeout(refresh, 50);
      });
    });

    // Fallback: catch variant swaps that do not touch the DOM we watch.
    setInterval(function () {
      var id = getVariantId(document);
      if (id !== lastVariantId) {
        lastVariantId = id;
        refresh();
      }
    }, 800);
  }

  function init() {
    whenPreproductReady(function () {
      lastVariantId = getVariantId(document);
      refresh();
      watch();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();